/**
 * `fleet_nodes.live_json` 的形狀。身體組這個物件，監控頁用同一組鍵畫摘要。
 * 這個檔不碰 Node API，console 可以直接 import。
 */

export const DEPLOY_SHA_FILES = ['/app/deploy/.deploy-sha', '/deploy/.deploy-sha'] as const;

export function resolveDeploySha(
  envValue: string | undefined,
  read: (path: string) => string | null,
): string {
  const fromEnv = (envValue ?? '').trim();
  if (fromEnv !== '') return fromEnv;
  for (const path of DEPLOY_SHA_FILES) {
    const text = read(path);
    if (text === null) continue;
    const trimmed = text.trim();
    if (trimmed !== '') return trimmed;
  }
  return '';
}

export type LiveRead = 'ok' | 'bad' | 'missing';

export type UtLiveRoom = {
  id: string;
  name: string;
  online: number;
  claimed: boolean;
};

export type UtLive = {
  platform: 'ut';
  rooms: UtLiveRoom[];
  coverage: { claimed: number; uncovered: number };
};

export type Ek21LiveRoom = {
  id: string;
  online: number;
  real: number;
  empty: boolean;
  cooling: boolean;
};

export type Ek21Live = {
  platform: 'ek21';
  rooms: Ek21LiveRoom[];
};

export type WootalkLive = {
  platform: 'wootalk';
  capsolver: { attempts: number; ok: number; throttled: number; budgetLeft: number };
  proxyDegraded: boolean;
  matches: number;
};

export type JdLive = {
  platform: 'jd';
  health: {
    alert: boolean;
    alertReason: string;
    authFails: number;
    lastInboundTs: string | null;
  };
};

export type SiteLive = {
  platform: 'site';
  state: 'up' | 'idle';
};

export type FleetLive = UtLive | Ek21Live | WootalkLive | JdLive | SiteLive;

function countText(ids: readonly string[]): number {
  let n = 0;
  for (const id of ids) {
    if (id.trim() !== '') n += 1;
  }
  return n;
}

function whole(value: number | undefined): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) return 0;
  return Math.max(0, Math.trunc(value));
}

export function utLive(input: {
  rooms: ReadonlyArray<{ id: string; name?: string; online?: number }>;
  claimedIds: readonly string[];
  uncoveredIds: readonly string[];
}): UtLive {
  const claimed = new Set<string>();
  for (const id of input.claimedIds) {
    const trimmed = id.trim();
    if (trimmed !== '') claimed.add(trimmed);
  }
  const rooms: UtLiveRoom[] = [];
  const seen = new Set<string>();
  for (const room of input.rooms) {
    const id = room.id.trim();
    if (id === '' || seen.has(id)) continue;
    seen.add(id);
    rooms.push({
      id,
      name: (room.name ?? '').trim(),
      online: whole(room.online),
      claimed: claimed.has(id),
    });
  }
  return {
    platform: 'ut',
    rooms,
    coverage: {
      claimed: countText(input.claimedIds),
      uncovered: countText(input.uncoveredIds),
    },
  };
}

export function ek21Live(
  rooms: ReadonlyArray<{
    id: string;
    online?: number;
    real?: number;
    empty?: boolean;
    cooling?: boolean;
  }>,
): Ek21Live {
  const out: Ek21LiveRoom[] = [];
  const seen = new Set<string>();
  for (const room of rooms) {
    const id = room.id.trim();
    if (id === '' || seen.has(id)) continue;
    seen.add(id);
    const real = whole(room.real);
    out.push({
      id,
      online: whole(room.online),
      real,
      empty: room.empty === true || real <= 0,
      cooling: room.cooling === true,
    });
  }
  return { platform: 'ek21', rooms: out };
}

export function wootalkLive(input: {
  attempts: number;
  ok: number;
  throttled: number;
  budgetLeft: number;
  proxyDegraded: boolean;
  matches: number;
}): WootalkLive {
  return {
    platform: 'wootalk',
    capsolver: {
      attempts: whole(input.attempts),
      ok: whole(input.ok),
      throttled: whole(input.throttled),
      budgetLeft: whole(input.budgetLeft),
    },
    proxyDegraded: input.proxyDegraded === true,
    matches: whole(input.matches),
  };
}

export function jdLive(input: {
  alert: boolean;
  alertReason: string;
  authFails: number;
  lastInboundTs: string | null;
}): JdLive {
  const last = input.lastInboundTs?.trim() ?? '';
  return {
    platform: 'jd',
    health: {
      alert: input.alert === true,
      alertReason: input.alert ? input.alertReason.trim() : '',
      authFails: whole(input.authFails),
      lastInboundTs: last === '' ? null : last,
    },
  };
}

export function siteLive(busy: boolean): SiteLive {
  return { platform: 'site', state: busy ? 'up' : 'idle' };
}

type FleetPlatform = FleetLive['platform'];

function platformOf(value: unknown): FleetPlatform | null {
  if (value === 'ut' || value === 'ek21' || value === 'wootalk' || value === 'jd' || value === 'site') {
    return value;
  }
  return null;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function asNumber(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

function roomIds(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  const ids: string[] = [];
  for (const row of value) {
    const record = asRecord(row);
    const id = record && typeof record.id === 'string' ? record.id.trim() : '';
    if (id !== '') ids.push(id);
  }
  return ids;
}

export function summarizeFleetLive(live: Record<string, unknown>, status: LiveRead): string {
  if (status === 'bad') return '無法讀取';
  if (status === 'missing') return '';
  const platform = platformOf(live.platform);
  if (platform === null) return Object.keys(live).length === 0 ? '' : '有即時狀態';
  switch (platform) {
    case 'ut': {
      const coverage = asRecord(live.coverage);
      const claimed = asNumber(coverage?.claimed);
      const uncovered = asNumber(coverage?.uncovered);
      return `UT 已佔 ${claimed} 未佔 ${uncovered}`;
    }
    case 'ek21': {
      const ids = roomIds(live.rooms);
      return ids.length > 0 ? `EK21 ${ids.join(',')}` : 'EK21 沒有房間';
    }
    case 'wootalk': {
      const cap = asRecord(live.capsolver);
      const ok = asNumber(cap?.ok);
      const matches = asNumber(live.matches);
      const proxy = live.proxyDegraded === true ? '代理降級' : '代理正常';
      return `WooTalk 配對 ${matches} CapSolver ${ok} ${proxy}`;
    }
    case 'jd': {
      const health = asRecord(live.health);
      if (health?.alert === true) {
        const reason = typeof health.alertReason === 'string' ? health.alertReason.trim() : '';
        return reason === '' ? 'JD 告警' : `JD 告警 ${reason}`;
      }
      return 'JD 正常';
    }
    case 'site':
      if (live.state === 'idle') return 'site 閒置';
      if (live.state === 'up') return 'site 運作';
      return '';
    default: {
      const neverPlatform: never = platform;
      return neverPlatform;
    }
  }
}
